import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Background as BackgroundElem, BackgroundInner as BackgroundInnerElem } from './HeroSlideshow.styles';

const HeroSlideshow = ({ carousel, extraClassNames, heroHeight }) => {
  const [curr, setCurr] = useState(0);
  const { length } = carousel;

  const goToNext = () => {
    // Check if we've reached the final slide in the array
    // If so, go back to 0, else curr + 1
    setCurr(curr === length - 1 ? 0 : curr + 1);
  };

  const getDuration = () => {
    return carousel[curr].field === 'video' ? carousel[curr].duration * 1000 : 5000;
  };

  // useEffect will run at every re-render
  useEffect(() => {
    // At every render, set a new timeout to go to the next slide
    setTimeout(goToNext, getDuration());
    // And, when unmounting <Slider />, clear the timeout
    // See the reactjs.org docs on hooks for more info
    return () => {
      clearTimeout(goToNext);
    };
  });

  if (!Array.isArray(carousel) || length <= 0) {
    return null;
  }

  const renderSlide = ({ id, field, image, url }, index) => {
    return field === 'video' ? (
      <video
        key={id}
        className={`object-cover w-full h-auto absolute inset-0 ${curr !== index && 'hidden'}`}
        src={url}
        type="video/mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
      />
    ) : (
      <BackgroundInnerElem
        key={id}
        xs={image.xs}
        sm={image.sm}
        md={image.md}
        xl={image.xl}
        aria-label={image.title}
        className={`bg-center bg-no-repeat bg-cover h-full w-full absolute inset-0 ${curr !== index && 'hidden'}`}
      />
    );
  };

  return (
    <>
      {carousel.map(
        (carouselItem) =>
          carouselItem.url && (
            <link key={carouselItem.url} rel="preload" href={carouselItem.url} as={carouselItem.field} />
          ),
      )}
      <BackgroundElem className={`relative h-screen-90 w-full ${extraClassNames}`} heroHeight={heroHeight}>
        <TransitionGroup data-test-id="hero">
          <CSSTransition key={`hero-slide-${curr}`} timeout={getDuration()} classNames="cross-fade">
            <div>{carousel.map((slide, index) => renderSlide(slide, index))}</div>
          </CSSTransition>
        </TransitionGroup>
      </BackgroundElem>
    </>
  );
};

HeroSlideshow.propTypes = {
  carousel: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.string,
        field: PropTypes.string,
        title: PropTypes.string,
        image: PropTypes.shape(),
      }),
      PropTypes.shape({
        id: PropTypes.string,
        field: PropTypes.string,
        url: PropTypes.string,
        duration: PropTypes.string,
      }),
    ]),
  ).isRequired,
  extraClassNames: PropTypes.string,
  heroHeight: PropTypes.shape({}),
};

HeroSlideshow.defaultProps = {
  extraClassNames: '',
  heroHeight: null,
};

export default HeroSlideshow;
